import { useCallback } from 'react';

import { FabricChannel } from '@atlaskit/analytics-listeners';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

interface RovoButtonAnalyticsEventPayload {
	action: string;
	actionSubject: 'rovoButton';
	actionSubjectId: string;
	attributes?: Record<string, any>;
	channel?: string;
	product: string;
	source: string;
}

interface RovoButtonJiraAnalyticsEvent {
	action: string;
	actionSubject: 'rovoButton';
	actionSubjectId: string;
	attributes?: Record<string, any>;
}

interface RovoButtonConfluenceAnalyticsEvent {
	data: RovoButtonAnalyticsEventPayload;
	type: 'sendUIEvent';
}

interface RovoButtonDefaultAnalyticsEvent extends RovoButtonAnalyticsEventPayload {
	eventType: 'ui';
}

type RovoButtonAnalyticsEvent =
	| RovoButtonJiraAnalyticsEvent
	| RovoButtonConfluenceAnalyticsEvent
	| RovoButtonDefaultAnalyticsEvent;

const ANALYTICS_CHANNEL = FabricChannel.aiMate;

export const useRovoButtonEventsInstrumentation = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireEventWithOptionalChannel = useCallback(
		(event: RovoButtonAnalyticsEvent, product: string) => {
			const analyticsEvent = createAnalyticsEvent(event);

			if (product === 'confluence') {
				analyticsEvent.fire();
				return;
			}

			// it goes thru the aiMate analytics listener to avoid validation per product listener
			// https://bitbucket.org/atlassian/atlassian-frontend-mirror/src/f904f690cf57ef84acad6c7bf1b5bd90ad9cada2/analytics/analytics-listeners/src/atlaskit/process-event.ts#lines-58
			analyticsEvent.fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const createEventPayload = useCallback(
		(payload: RovoButtonAnalyticsEventPayload): RovoButtonAnalyticsEvent => {
			if (payload.product === 'confluence') {
				// confluence event validation frontend logic
				// https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/f8bf8ba8c7b54c6e2764d85551b3f989b69d28d8/confluence/next/packages/analytics/src/analyticsListener.tsx#lines-53
				return {
					type: 'sendUIEvent',
					data: payload,
				} as RovoButtonConfluenceAnalyticsEvent;
			}

			if (payload.product === 'jira') {
				// jira event validation frontend logic
				// https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/f8bf8ba8c7b54c6e2764d85551b3f989b69d28d8/jira/src/packages/platform/observability/product-analytics-bridge/src/utils/extract-data-from-event.tsx#lines-164
				// it requires to use no more than 4 properties on the top level of event shape for custom event fire.
				return {
					actionSubject: payload.actionSubject,
					action: payload.action,
					actionSubjectId: payload.actionSubjectId,
					attributes: payload.attributes,
				} as RovoButtonJiraAnalyticsEvent;
			}

			// for any other product type we should use the default event shape
			// also if there is a custom event channel, we also should use the default event shape
			return {
				eventType: 'ui',
				...payload,
			} as RovoButtonDefaultAnalyticsEvent;
		},
		[],
	);

	const fireRovoButtonUIEvent = useCallback(
		(eventData: RovoButtonAnalyticsEventPayload): void => {
			// Only fire the event if product is defined
			if (!eventData.product) {
				return;
			}

			const event = createEventPayload(eventData);
			fireEventWithOptionalChannel(event, eventData.product);
		},
		[fireEventWithOptionalChannel, createEventPayload],
	);

	return {
		fireRovoButtonUIEvent,
	};
};
